import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  webpSrc?: string;
  srcSet?: string;
  sizes?: string;
  blurDataURL?: string;
  priority?: boolean;
  fetchPriority?: "high" | "low" | "auto";
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  hideLoadingSpinner?: boolean;
  /** Enable automatic _pc/_mobile variant detection (like ResponsiveImage) */
  responsive?: boolean;
  /** Custom rootMargin for Intersection Observer (defaults to "50px" for backward compatibility) */
  rootMargin?: string;
  /** For content images that should maintain aspect ratio (uses h-auto instead of h-full) */
  contentImage?: boolean;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  fallbackSrc,
  webpSrc,
  srcSet,
  sizes,
  blurDataURL,
  priority = false,
  fetchPriority,
  className,
  onLoad,
  onError,
  hideLoadingSpinner = false,
  responsive = false,
  rootMargin = "50px",
  contentImage = false,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [retryCount, setRetryCount] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showLQIP, setShowLQIP] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Generate responsive image sources for _pc/_mobile variants
  const getResponsiveSrc = (baseSrc: string) => {
    if (baseSrc.includes("_mobile.webp")) {
      return baseSrc;
    }
    if (baseSrc.includes("_pc.webp")) {
      return baseSrc.replace("_pc.webp", "_mobile.webp");
    }
    const lastDotIndex = baseSrc.lastIndexOf(".");
    if (lastDotIndex === -1) return baseSrc;
    const basePath = baseSrc.substring(0, lastDotIndex);
    const extension = baseSrc.substring(lastDotIndex);
    return `${basePath}_mobile${extension}`;
  };

  const getPCSrc = (baseSrc: string) => {
    if (baseSrc.includes("_pc.webp")) {
      return baseSrc;
    }
    if (baseSrc.includes("_mobile.webp")) {
      return baseSrc.replace("_mobile.webp", "_pc.webp");
    }
    const lastDotIndex = baseSrc.lastIndexOf(".");
    if (lastDotIndex === -1) return baseSrc;
    const basePath = baseSrc.substring(0, lastDotIndex);
    const extension = baseSrc.substring(lastDotIndex);
    return `${basePath}_pc${extension}`;
  };

  // Detect screen size for responsive variants
  useEffect(() => {
    if (!responsive) return;

    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, [responsive]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) {
      setIsInView(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: rootMargin, // Customizable rootMargin (defaults to "50px" for backward compatibility)
        threshold: 0.1,
      },
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority, rootMargin]);

  const handleLoad = () => {
    setIsLoaded(true);
    // Delay hiding LQIP for smoother transition (300ms delay like HTML demo)
    setTimeout(() => {
      setShowLQIP(false);
    }, 300);
    onLoad?.();
  };

  const handleError = () => {
    // Try to retry loading the image once
    if (retryCount < 1 && imgRef.current) {
      setRetryCount((prev) => prev + 1);
      // Force reload by adding a timestamp parameter
      const separator = src.includes("?") ? "&" : "?";
      const retrySrc = `${src}${separator}t=${Date.now()}`;
      const encodedRetrySrc = retrySrc.includes(' ') ? encodeURI(retrySrc) : retrySrc;

      // Create a new image to test the retry
      const retryImg = new Image();
      retryImg.onload = () => {
        // If retry succeeds, update the src
        if (imgRef.current) {
          imgRef.current.src = encodedRetrySrc;
        }
      };
      retryImg.onerror = () => {
        // If retry fails, show error state
        setHasError(true);
        onError?.();
      };
      retryImg.src = encodedRetrySrc;
    } else {
      setHasError(true);
      onError?.();
    }
  };

  // Encode URLs with spaces for proper browser handling
  const encodeIfNeeded = (url: string) => {
    return url.includes(' ') ? encodeURI(url) : url;
  };

  // Encode URLs in srcset string (handles multiple URLs separated by commas)
  const encodeSrcSet = (srcSetString?: string): string | undefined => {
    if (!srcSetString) return undefined;
    // Split by comma, encode each URL part, then rejoin
    return srcSetString.split(',').map(item => {
      const trimmed = item.trim();
      // Find the descriptor (e.g., "400w", "2x") at the end
      const descriptorMatch = trimmed.match(/\s+(\d+[wx])$/);
      if (descriptorMatch) {
        const urlPart = trimmed.substring(0, descriptorMatch.index).trim();
        const descriptor = descriptorMatch[1];
        return `${encodeIfNeeded(urlPart)} ${descriptor}`;
      }
      // If no descriptor found, just encode the whole thing
      return encodeIfNeeded(trimmed);
    }).join(', ');
  };

  // Determine the final source based on responsive mode and error state
  let finalSrc = hasError && fallbackSrc ? fallbackSrc : src;
  
  if (responsive && !hasError) {
    const mobileSrc = getResponsiveSrc(src);
    const pcSrc = getPCSrc(src);
    finalSrc = isDesktop ? pcSrc : mobileSrc;
  }

  return (
    <div
      className={cn("relative overflow-hidden", className)}
      data-oid="yg433z4"
    >
      {/* Blur Placeholder */}
      {blurDataURL && !hasError && (
        <div
          className={cn(
            "absolute inset-0 z-10 transition-opacity duration-500 ease",
            isLoaded && !showLQIP ? "opacity-0 pointer-events-none" : "opacity-100"
          )}
          style={{
            backgroundImage: `url(${blurDataURL})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(20px)",
            transform: "scale(1.1)", // Avoid bleed
          }}
          data-oid="blur-placeholder"
        />
      )}

      {/* Loading spinner fallback if no blurDataURL */}
      {!blurDataURL && !isLoaded && !hasError && !hideLoadingSpinner && (
        <div
          className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center"
          data-oid="3wsadda"
        >
          <div
            className="w-8 h-8 border-2 border-gray-300 border-t-alpine-gold rounded-full animate-spin"
            data-oid="gvfcf_e"
          />
        </div>
      )}

      {/* Image element */}
      {isInView && (
        <picture data-oid="image-picture">
          {webpSrc && <source srcSet={encodeSrcSet(webpSrc)} type="image/webp" />}
          <img
            ref={imgRef}
            key={responsive ? `${finalSrc}-${isDesktop}` : finalSrc}
            src={encodeIfNeeded(finalSrc)}
            srcSet={encodeSrcSet(srcSet)}
            sizes={sizes}
            alt={alt}
            className={cn(
              "w-full transition-all ease",
              contentImage ? "h-auto" : "h-full object-cover",
              isLoaded 
                ? "opacity-100 scale-100 blur-0 duration-500" 
                : hideLoadingSpinner 
                  ? "opacity-100" 
                  : "opacity-0 scale-105 blur-[10px] duration-500",
              priority ? "duration-300" : "duration-500"
            )}
            loading={priority ? "eager" : "lazy"}
            fetchpriority={fetchPriority}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            {...props}
            data-oid="aw_1lt9"
          />
        </picture>
      )}

      {/* Error state */}
      {hasError && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
          data-oid="xfuzq-v"
        >
          <div className="text-center text-gray-500" data-oid="ni5lyqo">
            <div
              className="w-16 h-16 mx-auto mb-3 bg-gray-300 rounded-lg flex items-center justify-center"
              data-oid="tctg67x"
            >
              <svg
                className="w-8 h-8 opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                data-oid="i9_cns_"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  data-oid="yhip3u2"
                />
              </svg>
            </div>
            <p className="text-sm font-medium" data-oid="6h48k1j">
              Image unavailable
            </p>
            <button
              onClick={() => {
                setHasError(false);
                setRetryCount(0);
                setIsLoaded(false);
              }}
              className="mt-2 px-3 py-1 bg-alpine-gold text-white text-xs rounded-md hover:bg-alpine-gold/90 transition-colors"
              data-oid="cvqlyb6"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
